import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

const EMPTY_FORM = { networkId: '', providerName: '', url: '', priority: 1 };
const STATUSES = ['ACTIVE', 'INACTIVE', 'DOWN'];

export default function RpcProviders() {
  const [providers, setProviders] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    apiCall(client.get('/rpc_provider/admin/get_list'))
      .then(setProviders)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    apiCall(client.get('/network/admin/get_list')).then(setNetworks).catch(() => setNetworks([]));
  };
  useEffect(load, []);

  const networkName = (id) => networks.find((n) => n.uniqueId === id)?.name || id;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.post('/rpc_provider/admin/create', { ...form, priority: +form.priority }));
      setForm(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.put('/rpc_provider/admin/update', { ...editing, priority: +editing.priority }));
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cycleStatus = async (row) => {
    const next = STATUSES[(STATUSES.indexOf(row.status) + 1) % STATUSES.length];
    try {
      await apiCall(client.patch('/rpc_provider/admin/update_status', { uniqueId: row.uniqueId, status: next }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: 'providerName', label: 'Provider' },
    { key: 'networkId', label: 'Network', render: (row) => networkName(row.networkId) },
    { key: 'url', label: 'URL', render: (row) => <code>{row.url}</code> },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    {
      key: 'actions', label: '', render: (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setEditing(row)}>Edit</button>
          <button className="btn btn-outline btn-sm" onClick={() => cycleStatus(row)}>Cycle status</button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <div>
          <h1>RPC Providers</h1>
          <p className="form-hint" style={{ marginTop: 4 }}>
            Providers are tried in ascending priority order; if one fails, the next is used automatically.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }}>+ Add Provider</button>
      </div>
      {error && <div className="form-error">{error}</div>}

      <div className="card">
        {loading ? (
          <Loader label="Loading RPC providers..." />
        ) : (
          <DataTable columns={columns} rows={providers} emptyMessage="No RPC providers configured yet." />
        )}
      </div>

      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Add provider</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Network</label>
                <select name="networkId" required value={form.networkId} onChange={handleChange}>
                  <option value="">Select network</option>
                  {networks.map((n) => <option key={n.uniqueId} value={n.uniqueId}>{n.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Provider name</label>
                <input name="providerName" required value={form.providerName} onChange={handleChange} placeholder="Alchemy" /></div>
              <div className="form-group"><label>RPC URL</label>
                <input name="url" required value={form.url} onChange={handleChange} placeholder="https://..." /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Priority</label>
                <input name="priority" type="number" min="1" value={form.priority} onChange={handleChange} /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Edit provider</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group"><label>Provider name</label>
                <input required value={editing.providerName} onChange={(e) => setEditing({ ...editing, providerName: e.target.value })} /></div>
              <div className="form-group"><label>RPC URL</label>
                <input required value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Priority</label>
                <input type="number" min="1" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>Save</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
